/**
 * THE DELIVERY DETAIL, OVER THE LOG. On desktop and tablet the log stays mounted in the parent
 * layout and this route renders the detail in a panel above it; closing is a navigation back to
 * `/deliveries` with the log's own search intact, so nothing behind it reloads.
 *
 * A phone gets no sliding panel: the detail is the whole screen, with its own back link.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { usePlatform } from "../shell/usePlatform";
import { RoutePage, routeEntry } from "./routeTable";

const entry = routeEntry("/deliveries/:deliveryId");

export const Route = createFileRoute("/deliveries/$deliveryId")({
  head: () => ({
    meta: [
      { title: "Feed delivery detail — LevelLoad" },
      { name: "description", content: "Feed delivery details and processing issues in LevelLoad." },
      { property: "og:title", content: "Feed delivery detail — LevelLoad" },
      {
        property: "og:description",
        content: "Feed delivery details and processing issues in LevelLoad.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: RouteComponent,
});

/* No route in this app declares `validateSearch`, so this is the router's own type for an
   unvalidated route's search -- sourced from `Route` itself, not guessed, so it tracks a future
   validator automatically. */
type DetailSearch = (typeof Route)["types"]["fullSearchSchema"];

function RouteComponent() {
  const navigate = useNavigate();
  const platform = usePlatform();

  if (platform === "phone") return <RoutePage entry={entry} />;

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        /* The log's filters ride the same search, so closing keeps them. */
        if (!open) void navigate({ to: "/deliveries", search: (prev: DetailSearch) => prev });
      }}
    >
      <SheetContent
        side="right"
        className="flex w-[min(64rem,92vw)] flex-col overflow-y-auto p-0 sm:max-w-none"
      >
        <SheetTitle className="sr-only">Feed delivery detail</SheetTitle>
        <RoutePage entry={entry} />
      </SheetContent>
    </Sheet>
  );
}
